import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Send, Plus, ExternalLink } from "lucide-react";

const CATEGORIES = [
  "Marketing",
  "AI & Tools",
  "Local Activation",
  "Visibility",
  "Advertising",
  "P&L Training",
  "Community",
  "Operations",
];

export default function DashBlog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [category, setCategory] = useState("Marketing");
  const [drafting, setDrafting] = useState(false);

  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    meta_description: "",
    body: "",
    cover_image: "",
    read_time: 7,
    tags: "",
    published: true,
  });
  const [saving, setSaving] = useState(false);

  const [posts, setPosts] = useState([]);

  const loadPosts = async () => {
    try {
      const { data } = await api.get("/blog", { params: { limit: 30 } });
      setPosts(data);
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  if (!isAdmin) {
    // Members must never see (or land on) the editor. Silently redirect home.
    return <Navigate to="/dashboard" replace />;
  }

  const onDraft = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return toast.error("Add a topic first.");
    setDrafting(true);
    try {
      const { data } = await api.post("/blog/draft", {
        topic,
        keywords,
        category,
      });
      setForm({
        title: data.title || "",
        slug: data.slug || "",
        excerpt: data.excerpt || "",
        meta_description: data.meta_description || "",
        body: data.body || "",
        cover_image: "",
        read_time: data.read_time || 7,
        tags: (data.tags || []).join(", "),
        published: true,
      });
      toast.success("Draft generated. Review and publish.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Draft failed.");
    } finally {
      setDrafting(false);
    }
  };

  const onPublish = async (e) => {
    e.preventDefault();
    if (!form.title || !form.slug || !form.body) {
      return toast.error("Title, slug and body are required.");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        category,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        read_time: Number(form.read_time) || 7,
      };
      await api.post("/blog", payload);
      toast.success("Post published.");
      setForm({
        title: "",
        slug: "",
        excerpt: "",
        meta_description: "",
        body: "",
        cover_image: "",
        read_time: 7,
        tags: "",
        published: true,
      });
      setTopic("");
      setKeywords("");
      loadPosts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Publish failed.");
    } finally {
      setSaving(false);
    }
  };

  const fld =
    "w-full rounded-lg px-4 py-2.5 text-sm font-medium outline-none transition-colors bg-white border border-[var(--lh-border)] text-[var(--lh-espresso)] focus:border-[var(--lh-peach)]";

  return (
    <div data-testid="dashboard-blog-page">
      <div className="overline text-[var(--lh-mauve)] mb-3">The Journal · Editor</div>
      <h1 className="font-serif-display text-5xl tracking-tighter leading-[0.95]">
        Draft, publish, <em>compound.</em>
      </h1>
      <p className="mt-5 text-base font-medium text-[var(--lh-text)] max-w-2xl">
        Generate a first draft with Claude, edit it like a human, and publish to the public
        journal. Every post is SEO-indexed and surfaces under <code>/blog</code>.
      </p>

      <div className="mt-10 grid lg:grid-cols-12 gap-8">
        {/* Draft generator */}
        <div className="lg:col-span-5">
          <div
            className="rounded-2xl p-7 border"
            style={{
              background: "var(--lh-mocha)",
              borderColor: "rgba(234,216,199,0.18)",
            }}
            data-testid="blog-draft-card"
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[0.65rem] font-semibold uppercase tracking-wider mb-4"
              style={{
                background: "rgba(255,168,115,0.15)",
                color: "var(--lh-peach)",
              }}
            >
              <Sparkles size={12} /> AI Draft
            </div>
            <h2 className="font-display-bold text-3xl text-[var(--lh-warm-ivory)] tracking-tighter">
              Start from a topic.
            </h2>
            <p className="mt-2 text-sm font-medium" style={{ color: "rgba(234,216,199,0.75)" }}>
              Claude writes a 700–1100 word SEO post in the LocalizeHER editorial voice.
            </p>

            <form onSubmit={onDraft} className="mt-6 space-y-4">
              <div>
                <div className="overline mb-2" style={{ color: "rgba(234,216,199,0.6)" }}>
                  Topic
                </div>
                <input
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g. How to price service packages for a Scottsdale studio"
                  className="w-full rounded-lg px-4 py-2.5 text-sm font-medium outline-none transition-colors"
                  style={{
                    background: "rgba(0,0,0,0.25)",
                    color: "var(--lh-warm-ivory)",
                    border: "1px solid rgba(234,216,199,0.18)",
                  }}
                  data-testid="blog-draft-topic"
                />
              </div>
              <div>
                <div className="overline mb-2" style={{ color: "rgba(234,216,199,0.6)" }}>
                  Target keywords
                </div>
                <input
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="women-owned business pricing, Scottsdale studio"
                  className="w-full rounded-lg px-4 py-2.5 text-sm font-medium outline-none transition-colors"
                  style={{
                    background: "rgba(0,0,0,0.25)",
                    color: "var(--lh-warm-ivory)",
                    border: "1px solid rgba(234,216,199,0.18)",
                  }}
                  data-testid="blog-draft-keywords"
                />
              </div>
              <div>
                <div className="overline mb-2" style={{ color: "rgba(234,216,199,0.6)" }}>
                  Category
                </div>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-lg px-4 py-2.5 text-sm font-medium outline-none transition-colors"
                  style={{
                    background: "rgba(0,0,0,0.25)",
                    color: "var(--lh-warm-ivory)",
                    border: "1px solid rgba(234,216,199,0.18)",
                  }}
                  data-testid="blog-draft-category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c} style={{ color: "#1F1A15" }}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={drafting}
                className="btn-mauve w-full justify-center"
                data-testid="blog-draft-generate"
              >
                {drafting ? "Drafting…" : "Generate draft"} <Sparkles size={14} />
              </button>
            </form>
          </div>

          {/* Recent posts */}
          <div className="mt-6">
            <div className="overline text-[var(--lh-muted)] mb-3">Recent</div>
            <div className="space-y-2">
              {posts.slice(0, 8).map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="block p-3 rounded-lg bg-white border border-[var(--lh-border)] hover:border-[var(--lh-peach)] transition-colors"
                >
                  <div className="text-xs uppercase tracking-wider text-[var(--lh-muted)] mb-1">
                    {p.category}
                  </div>
                  <div className="text-sm font-semibold text-[var(--lh-espresso)] leading-snug">
                    {p.title}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="lg:col-span-7">
          <form
            onSubmit={onPublish}
            className="rounded-2xl p-7 bg-white border border-[var(--lh-border)] space-y-4"
            data-testid="blog-editor-card"
          >
            <div>
              <div className="overline text-[var(--lh-muted)] mb-2">Title</div>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className={fld}
                placeholder="The headline of your piece"
                data-testid="blog-editor-title"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <div className="overline text-[var(--lh-muted)] mb-2">Slug</div>
                <input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className={fld}
                  placeholder="lowercase-kebab-case"
                  data-testid="blog-editor-slug"
                />
              </div>
              <div>
                <div className="overline text-[var(--lh-muted)] mb-2">Read time (min)</div>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={form.read_time}
                  onChange={(e) => setForm({ ...form, read_time: e.target.value })}
                  className={fld}
                  data-testid="blog-editor-readtime"
                />
              </div>
            </div>
            <div>
              <div className="overline text-[var(--lh-muted)] mb-2">Excerpt</div>
              <textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                rows={2}
                className={fld}
                placeholder="One-paragraph hook that appears in the listing and at the top of the post."
                data-testid="blog-editor-excerpt"
              />
            </div>
            <div>
              <div className="overline text-[var(--lh-muted)] mb-2">SEO meta description</div>
              <textarea
                value={form.meta_description}
                onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                rows={2}
                className={fld}
                placeholder="155–160 characters of search-engine description."
                data-testid="blog-editor-meta"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <div className="overline text-[var(--lh-muted)] mb-2">Cover image URL</div>
                <input
                  value={form.cover_image}
                  onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                  className={fld}
                  placeholder="https://…"
                  data-testid="blog-editor-cover"
                />
              </div>
              <div>
                <div className="overline text-[var(--lh-muted)] mb-2">Tags (comma separated)</div>
                <input
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className={fld}
                  placeholder="local marketing, visibility, SEO"
                  data-testid="blog-editor-tags"
                />
              </div>
            </div>
            <div>
              <div className="overline text-[var(--lh-muted)] mb-2">Body (markdown)</div>
              <textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={18}
                className={`${fld} font-mono text-[13px] leading-relaxed`}
                placeholder="## Section heading&#10;&#10;Write the post in markdown…"
                data-testid="blog-editor-body"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <label className="text-sm font-medium text-[var(--lh-espresso)] flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  data-testid="blog-editor-published"
                />
                Publish immediately
              </label>
              <button
                type="submit"
                disabled={saving}
                className="btn-mauve"
                data-testid="blog-editor-publish"
              >
                {saving ? "Saving…" : form.published ? "Publish post" : "Save draft"}{" "}
                {form.published ? <Send size={14} /> : <Plus size={14} />}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
